export type ScheduleConflictTrigger = "after-purchase" | "abandoned-purchase";

export interface ScheduleConflictDetails {
  trigger: ScheduleConflictTrigger;
  triggerLabel: string;
  delayLabel: string;
  offeringNames: string[];
}

export interface ScheduleConflictModalCopy {
  title: string;
  lead: string;
  offeringNames: string[];
  footerHint: string;
}

export const EMAIL_AUTOMATION_SAVE_ERROR_FALLBACK =
  "Failed to save email automation. Please try again.";

export function getApiErrorMessage(err: unknown): string {
  if (
    err &&
    typeof err === "object" &&
    "message" in err &&
    typeof (err as {message: unknown}).message === "string"
  ) {
    return (err as {message: string}).message;
  }
  return "";
}

function isScheduleConflictMessage(msg: string): boolean {
  const lower = msg.toLowerCase();
  return (
    lower.includes("offerings already have an automation") ||
    (lower.includes("automation with a") && lower.includes("delay already exists for")) ||
    lower.includes("please pick a different delay") ||
    (lower.includes("trigger") &&
      (lower.includes("delay") ||
        lower.includes("timing") ||
        lower.includes("schedule") ||
        lower.includes("required")))
  );
}

function parseTrigger(msg: string): ScheduleConflictTrigger | null {
  const match = msg.match(/trigger\s+"([^"]+)"/i);
  if (match?.[1] === "after-purchase" || match?.[1] === "abandoned-purchase") {
    return match[1];
  }
  if (/after-purchase/i.test(msg)) return "after-purchase";
  if (/abandoned-purchase/i.test(msg)) return "abandoned-purchase";
  return null;
}

function normalizeDelayFromUnit(value: number, unit: string): string | null {
  const normalizedUnit = unit.toLowerCase();

  if (normalizedUnit.startsWith("minute")) {
    if (value === 0) return null;
    return `${value / 60}h`;
  }
  if (normalizedUnit.startsWith("hour")) {
    if (value === 0) return null;
    return `${value}h`;
  }
  if (normalizedUnit.startsWith("day")) {
    if (value === 0) return null;
    return `${value * 24}h`;
  }

  return null;
}

function parseDelayRaw(msg: string): string | null {
  const legacyMatch = msg.match(/delay time of\s+([^.]+)\./i);
  if (legacyMatch?.[1]) {
    return legacyMatch[1].trim();
  }

  const newMatch = msg.match(
    /with a\s+(\d+(?:\.\d+)?)\s+(minutes?|hours?|days?)\s+delay/i
  );
  if (newMatch?.[1] && newMatch[2]) {
    return normalizeDelayFromUnit(Number(newMatch[1]), newMatch[2]);
  }

  return null;
}

function parseOfferingNames(msg: string): string[] {
  const newMatch = msg.match(/already exists for:\s*(.+?)(?:\.\s*Please|$)/i);
  if (newMatch?.[1]) {
    return newMatch[1]
      .split(",")
      .map((name) => name.trim())
      .filter(Boolean);
  }

  const legacyMatch = msg.match(/required:\s*(.+)$/i);
  if (!legacyMatch?.[1]) return [];
  return legacyMatch[1]
    .split(",")
    .map((name) => name.trim())
    .filter(Boolean);
}

function triggerDisplayLabel(trigger: ScheduleConflictTrigger): string {
  return trigger === "after-purchase" ? "After purchase" : "Abandoned purchase";
}

function delayDisplayLabel(
  trigger: ScheduleConflictTrigger,
  delayRaw: string | null
): string {
  if (trigger === "abandoned-purchase") {
    return "30 minutes after abandonment";
  }

  if (!delayRaw || delayRaw === "0h" || delayRaw === "0") {
    return "immediately";
  }

  const hoursMatch = delayRaw.match(/^(\d+(?:\.\d+)?)h$/i);
  if (hoursMatch) {
    const hours = Number(hoursMatch[1]);
    if (hours % 24 === 0 && hours >= 24) {
      const days = hours / 24;
      return `${days} ${days === 1 ? "day" : "days"} after purchase`;
    }
    if (hours >= 1) {
      return `${hours} ${hours === 1 ? "hour" : "hours"} after purchase`;
    }
    const mins = Math.round(hours * 60);
    return `${mins} ${mins === 1 ? "minute" : "minutes"} after purchase`;
  }

  return delayRaw;
}

export function parseScheduleConflictError(err: unknown): ScheduleConflictDetails | null {
  const msg = getApiErrorMessage(err);
  if (!msg || !isScheduleConflictMessage(msg)) return null;

  const trigger = parseTrigger(msg);
  if (!trigger) return null;

  const delayRaw = parseDelayRaw(msg);
  const offeringNames = parseOfferingNames(msg);

  return {
    trigger,
    triggerLabel: triggerDisplayLabel(trigger),
    delayLabel: delayDisplayLabel(trigger, delayRaw),
    offeringNames,
  };
}

export function buildScheduleConflictModalCopy(
  details: ScheduleConflictDetails
): ScheduleConflictModalCopy {
  const timingPhrase =
    details.delayLabel === "immediately" ? "immediately" : details.delayLabel;

  return {
    title: "Timing conflict on some offerings",
    lead: `The following offerings already have an ${details.triggerLabel.toLowerCase()} automation that sends ${timingPhrase}.`,
    offeringNames: details.offeringNames,
    footerHint:
      "Update the send time, pick different offerings, or edit the automation already linked to this offering.",
  };
}
